import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import SignaturePad from './SignaturePad.jsx';
import PdfFormFiller from './PdfFormFiller.jsx';
import { isFillablePdf, templatePdfUrl } from '../utils/pdfFormTemplate.js';
import './AthleteRegistration.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyAthlete = {
  name: '', dob: '', usag_id: '', height: '', weight: '', email: '', phone: '',
  emergency_contact_name: '', emergency_contact_phone: '',
};
const emptyParent = { name: '', email: '', phone: '', password: '', relationship: 'parent' };
const emptyInsurance = {
  insurance_provider: '', insurance_policy_number: '', insurance_group_number: '',
  primary_physician_name: '', primary_physician_phone: '',
  allergies: '', medical_history: '', orthopedic_injuries: '', surgical_procedures: '', current_medications: '',
};

const FORM_TYPE_LABEL = {
  liability: '⚖️ Liability', insurance: '🛡️ Insurance', medical: '🏥 Medical',
  photo_release: '📷 Photo Release', code_of_conduct: '📜 Code of Conduct', registration: '📝 Registration',
};

function AthleteRegistration() {
  const toast = useToast();
  const [athlete, setAthlete] = useState(emptyAthlete);
  const [parent, setParent] = useState(emptyParent);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState('form');        // 'form' | 'insurance' | 'waivers'
  const [regCtx, setRegCtx] = useState(null);        // { athleteId, athleteName, parentId, parentName, parentCreated, templates }
  const [insurance, setInsurance] = useState(emptyInsurance);
  const [sig, setSig] = useState({});                // { [templateId]: { name, agreed, signed, saving } }
  const [family, setFamily] = useState(null);        // success banner: { parentId, parentName, lastAthleteName, parentCreated, waivers }
  const [lookupQ, setLookupQ] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [lockedParent, setLockedParent] = useState(null); // returning athlete's existing parent
  const lookupTimer = useRef(null);
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [enrollClassId, setEnrollClassId] = useState(null);
  const [kioskPin, setKioskPin] = useState(() => localStorage.getItem('kioskPin') || '');
  const locked = !!kioskPin;

  // Load active classes once (for the program picker)
  useEffect(() => {
    api.get(`${API_BASE_URL}/api/classes?active_only=true`)
      .then(r => setClasses(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // ---- kiosk lock-down ----
  const lockKiosk = () => {
    const pin = window.prompt('Set a 4-digit staff PIN to lock the kiosk:');
    if (pin == null) return;
    if (/^\d{4}$/.test(pin)) { localStorage.setItem('kioskPin', pin); setKioskPin(pin); toast?.success?.('Kiosk locked — parents can register safely'); }
    else toast?.error?.('PIN must be exactly 4 digits');
  };
  const exitKiosk = () => {
    if (!locked) { navigate('/'); return; }
    const pin = window.prompt('Enter staff PIN to exit the kiosk:');
    if (pin == null) return;
    if (pin === kioskPin) { localStorage.removeItem('kioskPin'); setKioskPin(''); navigate('/'); }
    else toast?.error?.('Incorrect PIN');
  };
  const StaffBar = () => (
    <div className="reg-staffbar">
      <span className="reg-staff-brand">🤸 Registration Kiosk{locked && <span className="reg-lock-tag">🔒 Locked</span>}</span>
      <span className="reg-staff-actions">
        {locked
          ? <button className="reg-staff-btn" onClick={exitKiosk}>Staff exit</button>
          : <>
              <button className="reg-staff-btn" onClick={lockKiosk}>🔒 Lock kiosk</button>
              <button className="reg-staff-btn" onClick={exitKiosk}>Exit</button>
            </>}
      </span>
    </div>
  );

  const setA = (k, v) => setAthlete(a => ({ ...a, [k]: v }));
  const setP = (k, v) => setParent(p => ({ ...p, [k]: v }));
  const setI = (k, v) => setInsurance(i => ({ ...i, [k]: v }));

  const fetchRequiredWaivers = async () => {
    try {
      const res = await api.get(`${API_BASE_URL}/api/forms/templates?active_only=true`);
      return (res.data || [])
        // One control decides both inclusion and sequence: display_order 0 means
        // the club has not put this form in the kiosk run at all; 1..n is its
        // position. `is_required` is a separate concern (compliance tracking) and
        // deliberately does not put a form in front of a family here.
        .filter(t => (t.display_order ?? 0) > 0)
        .sort((a, b) =>
          (a.display_order ?? 0) - (b.display_order ?? 0) ||
          String(a.form_name).localeCompare(String(b.form_name))
        );
    } catch { return []; }
  };

  // ---- returning-athlete lookup (this gym only) ----
  const searchAthletes = (q) => {
    setLookupQ(q);
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (q.trim().length < 2) { setLookupResults([]); return; }
    lookupTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`${API_BASE_URL}/api/registration/find-athletes?q=${encodeURIComponent(q.trim())}`);
        setLookupResults(res.data || []);
      } catch { setLookupResults([]); }
    }, 250);
  };
  const pickAthlete = (a) => {
    setAthlete({
      id: a.id, name: a.name || '', dob: (a.dob || '').slice(0, 10), usag_id: a.usag_id || '',
      email: a.email || '', phone: a.phone || '', height: '', weight: '',
      emergency_contact_name: '', emergency_contact_phone: '',
    });
    setLookupResults([]); setLookupQ('');
    if (!family && a.parents && a.parents.length) {
      setLockedParent({ parentId: a.parents[0].id, parentName: a.parents[0].name });
    }
    toast?.success?.(`Loaded ${a.name} — review & update`);
  };
  const clearReturning = () => { setAthlete(emptyAthlete); setLockedParent(null); setLookupQ(''); setLookupResults([]); };

  // ---- step 1: register athlete + parent ----
  const submit = async (e) => {
    e.preventDefault();
    if (!athlete.name.trim()) { toast?.error?.("Enter the athlete's name"); return; }
    if (!athlete.dob) { toast?.error?.("Athlete date of birth is required"); return; }
    if (athlete.email && !EMAIL_RE.test(athlete.email.trim())) { toast?.error?.('Athlete email looks invalid'); return; }
    const reusedParentId = family ? family.parentId : (lockedParent ? lockedParent.parentId : null);
    if (!reusedParentId) {
      if (!parent.name.trim()) { toast?.error?.("Enter the parent's name"); return; }
      if (!EMAIL_RE.test(parent.email.trim())) { toast?.error?.('Enter a valid parent email'); return; }
      if (parent.phone.replace(/\D/g, '').length < 10) { toast?.error?.("Enter the parent's phone — we need at least one contact number"); return; }
      if (!parent.password || parent.password.length < 6) { toast?.error?.('Set a parent password (6+ characters)'); return; }
    }
    setSaving(true);
    try {
      const body = reusedParentId ? { athlete, parentId: reusedParentId } : { athlete, parent };
      if (enrollClassId) body.enrollClassId = enrollClassId;
      const res = await api.post(`${API_BASE_URL}/api/registration/onsite`, body);
      const parentName = family ? family.parentName : (lockedParent ? lockedParent.parentName : parent.name.trim());
      const parentCreated = reusedParentId ? false : res.data.parentCreated;
      const athleteName = athlete.name.trim();
      toast?.success?.(`${athleteName} ${res.data.athleteUpdated ? 'updated' : 'registered'} ✓`);

      const templates = await fetchRequiredWaivers();
      setRegCtx({ athleteId: res.data.athleteId, athleteName, parentId: res.data.parentId, parentName, parentCreated, templates, enrolledClassName: res.data.enrolledClassName });
      setInsurance(emptyInsurance);
      setAthlete(emptyAthlete);
      setLockedParent(null);
      setEnrollClassId(null);
      setStage('insurance');
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Registration failed');
    } finally {
      setSaving(false);
    }
  };

  // ---- step 2: insurance ----
  const afterInsurance = () => {
    if (regCtx.templates.length) {
      const initSig = {};
      regCtx.templates.forEach(t => { initSig[t.id] = { name: regCtx.parentName, agreed: false, signed: false, saving: false }; });
      setSig(initSig);
      setStage('waivers');
    } else {
      finishAll(0);
    }
  };
  const saveInsurance = async () => {
    setSaving(true);
    try {
      await api.post(`${API_BASE_URL}/api/athletes/${regCtx.athleteId}/insurance`, insurance);
      toast?.success?.('Insurance saved ✓');
      afterInsurance();
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Failed to save insurance');
    } finally {
      setSaving(false);
    }
  };

  // ---- step 3: waivers ----
  const setSigField = (id, k, v) => setSig(s => ({ ...s, [id]: { ...s[id], [k]: v } }));
  const signWaiver = async (t) => {
    const st = sig[t.id];
    if (!st?.agreed) { toast?.error?.('Check the agreement box first'); return; }
    if (!st?.name?.trim()) { toast?.error?.('Type the signer\'s printed name'); return; }
    if (!st?.signatureData) { toast?.error?.('Draw a signature to sign'); return; }
    setSigField(t.id, 'saving', true);
    try {
      await api.post(`${API_BASE_URL}/api/forms/submit`, {
        form_template_id: t.id, athlete_id: regCtx.athleteId, parent_id: regCtx.parentId,
        form_data: { agreed: true, signed_via: 'registration_kiosk', printed_name: st.name.trim() },
        signature_data: st.signatureData, signature_name: st.name.trim(),
      });
      setSigField(t.id, 'signed', true);
      toast?.success?.(`${t.form_name} signed ✓`);
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Failed to save signature');
    } finally {
      setSigField(t.id, 'saving', false);
    }
  };

  // ---- finish → success banner, back to form ----
  const finishAll = (signedCount) => {
    setFamily({
      parentId: regCtx.parentId, parentName: regCtx.parentName,
      lastAthleteName: regCtx.athleteName, parentCreated: regCtx.parentCreated,
      waivers: signedCount, enrolledClassName: regCtx.enrolledClassName,
    });
    setRegCtx(null); setSig({}); setInsurance(emptyInsurance); setStage('form');
  };

  const newFamily = () => { setFamily(null); setLockedParent(null); setAthlete(emptyAthlete); setParent(emptyParent); };
  const anotherForFamily = () => { setAthlete(emptyAthlete); setLookupQ(''); setLookupResults([]); };

  // Step indicator
  const Steps = ({ current }) => (
    <div className="reg-steps">
      {[['form', '1', 'Register'], ['insurance', '2', 'Insurance'], ['waivers', '3', 'Waivers']].map(([id, n, label]) => (
        <div key={id} className={`reg-step ${current === id ? 'active' : ''} ${['form', 'insurance', 'waivers'].indexOf(current) > ['form', 'insurance', 'waivers'].indexOf(id) ? 'done' : ''}`}>
          <span className="reg-step-num">{n}</span><span className="reg-step-label">{label}</span>
        </div>
      ))}
    </div>
  );

  // ===================== INSURANCE VIEW =====================
  if (stage === 'insurance') {
    return (
      <div className="reg-kiosk">
        <StaffBar />
        <div className="reg-shell">
          <div className="reg-topbar">
            <h1>🛡️ Insurance &amp; Medical</h1>
            <p>For <strong>{regCtx.athleteName}</strong> · used only in case of injury</p>
          </div>
          <Steps current="insurance" />

          <section className="reg-card">
            <h2 className="reg-card-title">Health Insurance</h2>
            <label className="reg-field"><span>Insurance provider</span>
              <input value={insurance.insurance_provider} onChange={e => setI('insurance_provider', e.target.value)} placeholder="e.g. Blue Cross Blue Shield" autoFocus /></label>
            <div className="reg-row">
              <label className="reg-field"><span>Policy / Member #</span>
                <input value={insurance.insurance_policy_number} onChange={e => setI('insurance_policy_number', e.target.value)} placeholder="XZY123456789" /></label>
              <label className="reg-field"><span>Group #</span>
                <input value={insurance.insurance_group_number} onChange={e => setI('insurance_group_number', e.target.value)} placeholder="GRP0001" /></label>
            </div>
            <h2 className="reg-card-title" style={{ marginTop: 20 }}>Primary Physician (optional)</h2>
            <div className="reg-row">
              <label className="reg-field"><span>Doctor name</span>
                <input value={insurance.primary_physician_name} onChange={e => setI('primary_physician_name', e.target.value)} placeholder="Dr. Smith" /></label>
              <label className="reg-field"><span>Doctor phone</span>
                <input value={insurance.primary_physician_phone} onChange={e => setI('primary_physician_phone', e.target.value)} placeholder="(555) 123-4567" /></label>
            </div>
          </section>

          <section className="reg-card">
            <h2 className="reg-card-title">🏥 Medical (start) — optional</h2>
            <p className="reg-card-hint">A quick start for their medical profile. Separate multiple items with commas. You can add full detail later.</p>
            <div className="reg-row">
              <label className="reg-field"><span>Allergies</span>
                <input value={insurance.allergies} onChange={e => setI('allergies', e.target.value)} placeholder="peanuts, penicillin, bee stings" /></label>
              <label className="reg-field"><span>Medical history (asthma, diabetes…)</span>
                <input value={insurance.medical_history} onChange={e => setI('medical_history', e.target.value)} placeholder="asthma, type 1 diabetes" /></label>
            </div>
            <div className="reg-row">
              <label className="reg-field"><span>Previous orthopedic injuries</span>
                <textarea rows={2} value={insurance.orthopedic_injuries} onChange={e => setI('orthopedic_injuries', e.target.value)} placeholder="e.g. left ankle sprain (2023)" /></label>
              <label className="reg-field"><span>Surgical procedures</span>
                <textarea rows={2} value={insurance.surgical_procedures} onChange={e => setI('surgical_procedures', e.target.value)} placeholder="e.g. ACL reconstruction" /></label>
            </div>
            <label className="reg-field"><span>Medications (if they take any)</span>
              <textarea rows={2} value={insurance.current_medications} onChange={e => setI('current_medications', e.target.value)} placeholder="e.g. albuterol inhaler as needed — or leave blank" /></label>
          </section>

          <div className="reg-submit-bar">
            <button type="button" className="reg-btn-secondary" onClick={afterInsurance} disabled={saving}>Skip</button>
            <button type="button" className="reg-btn-primary" onClick={saveInsurance} disabled={saving}>
              {saving ? 'Saving…' : regCtx.templates.length ? 'Save & continue to waivers' : 'Save & finish'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===================== WAIVER SIGNING VIEW =====================
  if (stage === 'waivers') {
    const total = regCtx.templates.length;
    const signedCount = Object.values(sig).filter(s => s.signed).length;
    const allSigned = signedCount === total;
    return (
      <div className="reg-kiosk">
        <StaffBar />
        <div className="reg-shell">
          <div className="reg-topbar">
            <h1>✍️ Sign Required Waivers</h1>
            <p>For <strong>{regCtx.athleteName}</strong> · signed by {regCtx.parentName}</p>
          </div>
          <Steps current="waivers" />

          <div className="reg-waiver-progress">
            <span>{signedCount} of {total} signed</span>
            <div className="reg-progress-track"><div className="reg-progress-fill" style={{ width: `${(signedCount / total) * 100}%` }} /></div>
          </div>

          {/* Only one fillable PDF is mounted at a time. Rendering several at once
              means several PDFs decoding in parallel on an iPad and several
              signature pads on screen — slow, and genuinely unclear which one
              you are signing. Forms are worked through in order. */}
          {regCtx.templates.map(t => {
            const st = sig[t.id] || {};
            const fillable = isFillablePdf(t);
            const isActiveFillable =
              fillable && !st.signed &&
              regCtx.templates.find(x => isFillablePdf(x) && !(sig[x.id] || {}).signed)?.id === t.id;
            return (
              <section key={t.id} className={`reg-waiver-card ${st.signed ? 'signed' : ''}`}>
                <div className="reg-waiver-head">
                  <h2>{t.form_name}</h2>
                  <span className="reg-waiver-type">{FORM_TYPE_LABEL[t.form_type] || t.form_type}</span>
                </div>

                {st.signed ? (
                  <div className="reg-waiver-signed">✓ Signed by {st.name}</div>
                ) : isActiveFillable ? (
                  // A mapped PDF is filled in and signed on the document itself,
                  // prefilled from the details collected in steps 1 and 2.
                  <PdfFormFiller
                    template={t}
                    athleteId={regCtx.athleteId}
                    parentId={regCtx.parentId}
                    signerName={regCtx.parentName}
                    onSigned={(result) => {
                      setSigField(t.id, 'name', regCtx.parentName);
                      setSigField(t.id, 'signed', true);
                      // The signature is saved either way, but a missing PDF copy
                      // means there is no printable artifact — staff must know now,
                      // not when a coach goes looking for it before a meet.
                      if (result && result.hasSignedPdf === false) {
                        toast?.error?.(
                          `${t.form_name}: signature saved, but the filled PDF could not be generated. Tell an admin.`
                        );
                      } else {
                        toast?.success?.(`${t.form_name} signed ✓`);
                      }
                    }}
                  />
                ) : fillable ? (
                  <div className="reg-waiver-queued">
                    ⏳ Up next — finish the form above first.
                  </div>
                ) : (
                  <>
                    <div className="reg-waiver-body">
                      {t.content
                        ? <div className="reg-waiver-text">{t.content}</div>
                        : (
                          // An unmapped PDF must still be READ before it is signed —
                          // never collect a signature on a document nobody has seen.
                          <div className="reg-waiver-pdfview">
                            {templatePdfUrl(t, API_BASE_URL) ? (
                              <>
                                <iframe
                                  title={`${t.form_name} document`}
                                  src={templatePdfUrl(t, API_BASE_URL)}
                                  className="reg-waiver-frame"
                                />
                                <p className="reg-waiver-frame-note">
                                  📄 Scroll the document above to read it in full.{' '}
                                  <a href={templatePdfUrl(t, API_BASE_URL)} target="_blank" rel="noopener noreferrer">
                                    Open in a new tab
                                  </a>
                                  {' · '}
                                  <span className="reg-waiver-unmapped">
                                    Not set up for typing yet — an admin can map its fields so it can be
                                    filled in here.
                                  </span>
                                </p>
                              </>
                            ) : (
                              <p className="reg-waiver-missing">
                                ⚠️ This form has no document or text on file. Ask an admin to fix it
                                before collecting a signature.
                              </p>
                            )}
                          </div>
                        )}
                    </div>
                    <div className="reg-waiver-sign">
                      <label className="reg-agree">
                        <input type="checkbox" checked={!!st.agreed} onChange={e => setSigField(t.id, 'agreed', e.target.checked)} />
                        I have read and agree to the terms above.
                      </label>
                      <input className="reg-sign-input" placeholder="Printed name of signer"
                        value={st.name || ''} onChange={e => setSigField(t.id, 'name', e.target.value)} />
                      <SignaturePad onChange={(d) => setSigField(t.id, 'signatureData', d)} />
                      <button className="reg-btn-primary reg-sign-btn" disabled={st.saving || !st.agreed || !st.name?.trim() || !st.signatureData}
                        onClick={() => signWaiver(t)}>{st.saving ? 'Signing…' : '✍️ Sign waiver'}</button>
                    </div>
                  </>
                )}
              </section>
            );
          })}

          <div className="reg-submit-bar">
            {!allSigned && <button type="button" className="reg-btn-secondary" onClick={() => finishAll(signedCount)}>Skip remaining (admin)</button>}
            <button type="button" className="reg-btn-primary" onClick={() => finishAll(signedCount)} disabled={!allSigned && signedCount === 0}>
              {allSigned ? '✓ Finish registration' : 'Finish'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===================== REGISTRATION FORM VIEW =====================
  return (
    <div className="reg-kiosk">
      <div className="reg-shell">
        <div className="reg-topbar">
          <h1>🤸 Athlete Registration</h1>
          <p>Enter your athlete's information and a parent/guardian to create the account.</p>
        </div>
        <Steps current="form" />

        {family && (
          <div className="reg-success-banner">
            <div className="reg-success-text">
              <strong>✓ {family.lastAthleteName}</strong> registered and linked to <strong>{family.parentName}</strong>
              {family.parentCreated && <span className="reg-newacct"> · new parent login created</span>}
              {family.enrolledClassName && <span className="reg-newacct"> · enrolled in {family.enrolledClassName}</span>}
              {family.waivers > 0 && <span className="reg-newacct"> · {family.waivers} waiver{family.waivers === 1 ? '' : 's'} signed</span>}
            </div>
            <div className="reg-success-actions">
              <button type="button" className="reg-btn-ghost" onClick={newFamily}>＋ New family</button>
            </div>
          </div>
        )}

        <form onSubmit={submit}>
          <div className="reg-columns">
            <section className="reg-card">
              <div className="reg-card-titlerow">
                <h2 className="reg-card-title">🧍 Athlete Information</h2>
                {athlete.id && <span className="reg-update-badge">↻ Updating existing</span>}
              </div>

              {/* Returning-athlete search — this gym only */}
              <div className="reg-lookup">
                <input className="reg-lookup-input" placeholder="🔎 Returning athlete? Search by name…"
                  value={lookupQ} onChange={e => searchAthletes(e.target.value)} />
                {lookupResults.length > 0 && (
                  <div className="reg-lookup-results">
                    {lookupResults.map(a => (
                      <button type="button" key={a.id} className="reg-lookup-item" onClick={() => pickAthlete(a)}>
                        <span className="reg-lookup-name">{a.name}</span>
                        <span className="reg-lookup-meta">
                          {a.dob ? new Date(a.dob).toLocaleDateString() : 'no DOB'}
                          {a.parents?.length ? ` · ${a.parents[0].name}` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {athlete.id && (
                  <button type="button" className="reg-lookup-clear" onClick={clearReturning}>✕ Not this athlete — start new</button>
                )}
              </div>

              <label className="reg-field reg-required"><span>Full name</span>
                <input value={athlete.name} onChange={e => setA('name', e.target.value)} placeholder="Jordan Smith" /></label>
              <div className="reg-row">
                <label className="reg-field reg-required"><span>Date of birth</span>
                  <input type="date" value={athlete.dob} onChange={e => setA('dob', e.target.value)} /></label>
                <label className="reg-field"><span>USAG # (optional)</span>
                  <input value={athlete.usag_id} onChange={e => setA('usag_id', e.target.value)} placeholder="1234567" /></label>
              </div>
              <div className="reg-row">
                <label className="reg-field"><span>Height (in)</span>
                  <input type="number" step="0.1" value={athlete.height} onChange={e => setA('height', e.target.value)} /></label>
                <label className="reg-field"><span>Weight (lb)</span>
                  <input type="number" step="0.1" value={athlete.weight} onChange={e => setA('weight', e.target.value)} /></label>
              </div>
              <label className="reg-field"><span>Athlete email (optional)</span>
                <input type="email" value={athlete.email} onChange={e => setA('email', e.target.value)} placeholder="athlete@email.com" /></label>
              <label className="reg-field"><span>Athlete phone (optional)</span>
                <input value={athlete.phone} onChange={e => setA('phone', e.target.value)} placeholder="(555) 123-4567" /></label>
              <div className="reg-row">
                <label className="reg-field"><span>Emergency contact</span>
                  <input value={athlete.emergency_contact_name} onChange={e => setA('emergency_contact_name', e.target.value)} placeholder="Name" /></label>
                <label className="reg-field"><span>Emergency phone</span>
                  <input value={athlete.emergency_contact_phone} onChange={e => setA('emergency_contact_phone', e.target.value)} placeholder="(555) 987-6543" /></label>
              </div>
            </section>

            <section className="reg-card">
              <h2 className="reg-card-title">👪 Parent / Guardian</h2>
              {family ? (
                <div className="reg-locked-family">
                  <div className="reg-locked-avatar">{family.parentName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}</div>
                  <div>
                    <div className="reg-locked-name">{family.parentName}</div>
                    <div className="reg-locked-note">This athlete will link to this parent. Choose “New family” to switch parents.</div>
                  </div>
                </div>
              ) : lockedParent ? (
                <div className="reg-locked-family">
                  <div className="reg-locked-avatar">{lockedParent.parentName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div className="reg-locked-name">{lockedParent.parentName}</div>
                    <div className="reg-locked-note">Existing parent on file — this athlete will stay linked to them.</div>
                  </div>
                  <button type="button" className="reg-btn-ghost" onClick={() => setLockedParent(null)}>Change</button>
                </div>
              ) : (
                <>
                  <label className="reg-field reg-required"><span>Parent name</span>
                    <input value={parent.name} onChange={e => setP('name', e.target.value)} placeholder="Alex Smith" /></label>
                  <label className="reg-field reg-required"><span>Email (used to log in)</span>
                    <input type="email" value={parent.email} onChange={e => setP('email', e.target.value)} placeholder="parent@email.com" /></label>
                  <label className="reg-field reg-required"><span>Phone</span>
                    <input type="tel" value={parent.phone} onChange={e => setP('phone', e.target.value)} placeholder="(555) 123-4567" /></label>
                  <label className="reg-field reg-required"><span>Create password</span>
                    <input type="password" value={parent.password} onChange={e => setP('password', e.target.value)} placeholder="At least 6 characters" /></label>
                  <label className="reg-field"><span>Relationship</span>
                    <select value={parent.relationship} onChange={e => setP('relationship', e.target.value)}>
                      <option value="parent">Parent</option>
                      <option value="guardian">Guardian</option>
                    </select></label>
                  <p className="reg-hint">A parent login is created and linked to the athlete. Already registered this season? Enter the same email and we'll link to the existing account.</p>
                </>
              )}
            </section>
          </div>

          {/* Program / class selection */}
          {classes.length > 0 && (
            <section className="reg-card">
              <h2 className="reg-card-title">📚 Program / Class <span className="reg-optional">optional</span></h2>
              <div className="reg-class-grid">
                {classes.map(c => {
                  const full = c.max_capacity && c.current_enrollment >= c.max_capacity;
                  const on = enrollClassId === c.id;
                  return (
                    <button type="button" key={c.id}
                      className={`reg-class-opt ${on ? 'active' : ''} ${full ? 'full' : ''}`}
                      onClick={() => setEnrollClassId(on ? null : c.id)}>
                      <span className="reg-class-name">{c.class_name}{on && ' ✓'}</span>
                      <span className="reg-class-meta">
                        {c.level ? `Level ${c.level}` : c.program_type || ''}{c.age_range ? ` · ${c.age_range}` : ''}
                      </span>
                      <span className="reg-class-fee">
                        {Number(c.monthly_tuition) > 0 ? `$${Number(c.monthly_tuition).toFixed(0)}/mo` : 'No tuition set'}
                        {Number(c.registration_fee) > 0 ? ` · $${Number(c.registration_fee).toFixed(0)} reg` : ''}
                        {full ? ' · FULL' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <div className="reg-submit-bar">
            {family && <button type="button" className="reg-btn-secondary" onClick={anotherForFamily} disabled={saving}>Clear athlete</button>}
            <button type="submit" className="reg-btn-primary" disabled={saving}>
              {saving ? 'Registering…' : family ? `＋ Add athlete to ${family.parentName.split(' ')[0]}'s family` : 'Register & continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AthleteRegistration;
